'use client';

import { useRef, useState } from 'react';

import type { GroupEventSearchItem } from '@ps/api/types';
import { Button } from '@ps/ui/atoms/button';
import { Separator } from '@ps/ui/atoms/separator';
import {
  ChevronLeft,
  Clock,
  MapPin,
  MoreHorizontal,
  Pencil,
  Presentation,
  Trash2,
} from 'lucide-react';

import { formatDateWithOrdinal, formatTimeRangeCompact } from '../utils/format';

// ─── Component ─────────────────────────────────────────────────────────────

interface EventDetailProps {
  event: GroupEventSearchItem;
  onBack: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** When true the "Reserve your spot" button is hidden (public view renders it externally). */
  isPublic?: boolean;
}

export function EventDetail({
  event,
  onBack,
  onEdit,
  onDelete,
  isPublic,
}: EventDetailProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const dateLabel = formatDateWithOrdinal(event.startDate);
  const timeLabel = formatTimeRangeCompact(event.startDate, event.endDate);

  return (
    <div className="overflow-hidden rounded-[16px] border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onBack}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              aria-label="Back to events"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex h-10 w-10 items-center justify-center rounded-[7px] bg-gray-200/50 dark:bg-gray-700/50">
              <Presentation className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <p className="text-xs font-light text-gray-500 dark:text-gray-400">
                {dateLabel}
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {event.name}
              </p>
            </div>
          </div>

          {/* Three-dot menu (only in edit mode) */}
          {(onEdit || onDelete) && (
            <div className="relative" ref={menuRef}>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowMenu(previous => !previous)}
                className="h-6 w-6 rounded-full border border-gray-300 bg-gray-200 hover:bg-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                aria-label="Event actions"
              >
                <MoreHorizontal className="h-3 w-3 text-gray-500 dark:text-gray-400" />
              </Button>

              {showMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute top-8 right-0 z-20 flex flex-col gap-4 rounded-[6px] border border-gray-200 bg-gray-100 p-3 dark:border-gray-700 dark:bg-gray-800">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowMenu(false);
                          onEdit();
                        }}
                        className="flex items-center gap-2 text-xs font-light whitespace-nowrap text-gray-900 transition-colors hover:text-black dark:text-gray-300 dark:hover:text-white"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowMenu(false);
                          onDelete();
                        }}
                        className="flex items-center gap-2 text-xs font-light whitespace-nowrap text-red-500 transition-colors hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <p className="text-sm leading-[1.2] font-light text-gray-500 dark:text-gray-400">
            {event.description}
          </p>
        )}

        <Separator className="bg-gray-200 dark:bg-gray-700" />

        {/* Location & Time */}
        <div className="flex flex-col gap-3">
          {event.address && (
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {event.address}
              </p>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {timeLabel}
            </p>
          </div>
        </div>

        {/* Reserve button — only in non-public (edit/preview) as a disabled preview */}
        {!isPublic && (
          <Button
            variant="ghost"
            disabled
            className="bg-mint-green-300 hover:bg-mint-green-400 w-full rounded-[10px] py-2.5 text-center text-sm font-medium text-black opacity-60 transition-colors hover:text-black"
          >
            Reserve your spot
          </Button>
        )}
      </div>
    </div>
  );
}
